// client/src/features/utilities/search.ts — the URL state of SCR-UTIL-001 and SCR-UTIL-003 ·
// MAINT-008 (04 §9.2.3).
//
// §9.2.3 is normative for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered list is shareable, survives refresh, and Back is a
// filter-undo. `features/repairs/search.ts` is the shape this copies.
//
// The list's names are `GET /api/v1/utility-services`'s own — `q`, `propertyId`, `utilityType`,
// `whoPays`, `active` plus the 02 §7.4 paging — so the URL of the screen and the query string
// of the request are one vocabulary and `toListQuery` is a rename-free pass-through.
//
// THE DETAIL'S `tab` IS THE SECOND PARAM THE ENDPOINT DOES NOT HAVE, and it is here rather than
// in three route files because 04 §7's own tree gives SCR-UTIL-003 exactly one route file
// (`app/utilities/$serviceId/index.tsx`). A tab per child route would add screens §8.8's
// inventory does not carry; a tab in component state would break §5.10's guarantee that the
// active tab survives refresh and share. A search param is the third answer and the only one
// that keeps both (D-1121).

import { z } from 'zod';

import { UTILITY_RESPONSIBILITIES, UTILITY_TYPES } from './enums';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const utilitySearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  q: z.string().trim().optional(),
  propertyId: z.string().uuid().optional(),
  utilityType: z.enum(UTILITY_TYPES).optional(),
  whoPays: z.enum(UTILITY_RESPONSIBILITIES).optional(),
  /** Live services, retired ones, or absent for both — the endpoint's own tri-state. */
  active: z.coerce.boolean().optional(),
});

export type UtilitySearch = z.infer<typeof utilitySearchSchema>;

/**
 * Which filters are engaged — the empty state needs to know why it is empty. „Nimic nu se
 * potrivește" and „nu ai nicio utilitate" are different sentences and only one of them offers
 * „adaugă prima utilitate" (§5.11).
 */
export function hasActiveFilters(search: UtilitySearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.propertyId !== undefined ||
    search.utilityType !== undefined ||
    search.whoPays !== undefined ||
    search.active !== undefined
  );
}

/** Clearing filters returns to page 1 with nothing else carried across. */
export function clearedSearch(): UtilitySearch {
  return { page: 1, pageSize: 25 };
}

/**
 * SCR-UTIL-003's four tabs (04 §6.5 DETAIL-TABS), as a share-able URL value.
 *
 * `facturi` is the fourth and it arrived after the other three (D-2205); its label became
 * „Plăți" when the tab grew into the page where announced payments are validated (D-2352), and
 * the URL value was kept so that every link already written still opens it. It is a tab and not a
 * screen for the reason the header gives about the first three: §7's tree gives SCR-UTIL-003
 * one route file and §8.8's inventory carries three utilities screens, so a route per tab would
 * add a screen the inventory would then be wrong about. Its position is last because it is the
 * newest question asked of a meter, not the commonest.
 */
export const UTILITY_TABS = ['consum', 'citiri', 'detalii', 'facturi'] as const;

export type UtilityTab = (typeof UTILITY_TABS)[number];

/**
 * The detail's own state. `tab` defaults to the consumption chart, which is the question
 * SCR-UTIL-003 exists to answer; `readingsPage` pages the history without disturbing it, and
 * `readingId` is the deep-link target — a row the screen scrolls to and marks, so a chart
 * point, a notification or a colleague's link opens on the reading it names.
 */
export const utilityDetailSearchSchema = z.object({
  tab: z.enum(UTILITY_TABS).default('consum'),
  readingsPage: z.coerce.number().int().min(1).default(1),
  readingId: z.string().uuid().optional(),
  /** The „Plăți" tab's history page, beside `readingsPage` and for the same reason (D-2205). */
  billsPage: z.coerce.number().int().min(1).default(1),
});

export type UtilityDetailSearch = z.infer<typeof utilityDetailSearchSchema>;
